import React, { useState } from "react";
import { UserCog, Plus, X, ToggleLeft, ToggleRight, Search } from "lucide-react";
import { staffAPI } from "../services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import toast from "react-hot-toast";

const ROL_LABELS: Record<string,string> = {
  dt: "Director Técnico", pf: "Prep. Físico", fisio: "Fisioterapeuta",
  nutricion: "Nutricionista", psicologo: "Psicólogo", analista: "Analista", admin: "Administrador",
};
const ROL_OPTIONS = Object.entries(ROL_LABELS);

const ROL_COLORS: Record<string,string> = {
  dt:"bg-blue-50 text-blue-700", pf:"bg-violet-50 text-violet-700",
  fisio:"bg-green-50 text-green-700", nutricion:"bg-amber-50 text-amber-700",
  psicologo:"bg-pink-50 text-pink-700", analista:"bg-cyan-50 text-cyan-700",
  admin:"bg-red-50 text-red-700",
};

interface StaffForm {
  email: string; password: string; nombre: string; apellido: string;
  rol: string; especialidad: string; telefono: string;
}

const EMPTY_FORM: StaffForm = {
  email:"", password:"", nombre:"", apellido:"", rol:"dt", especialidad:"", telefono:""
};

export default function StaffPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [form, setForm] = useState<StaffForm>(EMPTY_FORM);
  const [showPwd, setShowPwd] = useState(false);

  const { data: staff = [], isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: () => staffAPI.listar().then(r => r.data),
  });

  const crearMutation = useMutation({
    mutationFn: (data: StaffForm) => staffAPI.crear(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success("Miembro de staff creado");
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Error al crear"),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      staffAPI.actualizar(id, { is_active: activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }),
    onError: () => toast.error("Error al actualizar"),
  });

  const filtrados = (staff as any[]).filter(s =>
    busqueda === "" ||
    `${s.nombre} ${s.apellido} ${s.email}`.toLowerCase().includes(busqueda.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    crearMutation.mutate(form);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <UserCog size={20} className="text-indigo-500"/>
              <h1 className="text-2xl font-bold text-gray-900">Staff / Entrenadores</h1>
            </div>
            <p className="text-gray-500 text-sm">{(staff as any[]).length} miembros del cuerpo técnico</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm">
            <Plus size={16}/> Agregar miembro
          </button>
        </div>

        {/* Buscador */}
        <div className="relative mb-6 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
        </div>

        {/* Modal creación */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white">
                <h2 className="font-bold text-gray-900 text-lg">Agregar miembro al staff</h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"><X size={18}/></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Nombre *</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.nombre} onChange={e=>setForm(f=>({...f, nombre:e.target.value}))} required placeholder="Santiago"/>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Apellido *</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.apellido} onChange={e=>setForm(f=>({...f, apellido:e.target.value}))} required placeholder="Morales"/>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Email *</label>
                  <input type="email" className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.email} onChange={e=>setForm(f=>({...f, email:e.target.value}))} required placeholder="dt@academia.com"/>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Contraseña *</label>
                  <div className="relative">
                    <input type={showPwd?"text":"password"} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={form.password} onChange={e=>setForm(f=>({...f, password:e.target.value}))} required placeholder="Mínimo 8 caracteres"/>
                    <button type="button" onClick={()=>setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 font-medium">
                      {showPwd?"Ocultar":"Mostrar"}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Rol *</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.rol} onChange={e=>setForm(f=>({...f, rol:e.target.value}))}>
                    {ROL_OPTIONS.map(([val, lbl])=><option key={val} value={val}>{lbl}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Teléfono</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.telefono} onChange={e=>setForm(f=>({...f, telefono:e.target.value}))} placeholder="+57 300 000 0000"/>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Especialidad</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.especialidad} onChange={e=>setForm(f=>({...f, especialidad:e.target.value}))} placeholder="Ej: psicología deportiva, nutrición clínica..."/>
                </div>
                <div className="col-span-1 sm:col-span-2 bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                  <strong>Nota:</strong> Se creará una cuenta de acceso con el email y contraseña indicados.
                  El miembro podrá iniciar sesión inmediatamente con estas credenciales.
                </div>
                <div className="col-span-1 sm:col-span-2 flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-5 py-2.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium">Cancelar</button>
                  <button type="submit" disabled={crearMutation.isPending}
                    className="px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-sm">
                    {crearMutation.isPending ? "Creando..." : "Crear miembro"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tabla */}
        {isLoading ? (
          <div className="space-y-3">
            {[1,2,3].map(i=>(
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-gray-200"/>
                <div className="flex-1"><div className="h-4 bg-gray-200 rounded w-1/3 mb-2"/><div className="h-3 bg-gray-100 rounded w-1/2"/></div>
              </div>
            ))}
          </div>
        ) : filtrados.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
            <UserCog size={40} className="text-gray-300 mx-auto mb-3"/>
            <p className="text-gray-500 font-medium">{busqueda ? "Sin resultados" : "Sin miembros de staff"}</p>
            <p className="text-gray-400 text-sm mt-1 mb-4">
              {busqueda ? "Intenta otra búsqueda" : "Agrega el primer entrenador o miembro del cuerpo técnico"}
            </p>
            {!busqueda && (
              <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                + Agregar miembro
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3.5 font-semibold text-gray-600 text-xs uppercase tracking-wide">Miembro</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-gray-600 text-xs uppercase tracking-wide">Rol</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-gray-600 text-xs uppercase tracking-wide hidden md:table-cell">Email</th>
                  <th className="text-center px-5 py-3.5 font-semibold text-gray-600 text-xs uppercase tracking-wide">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtrados.map((s: any) => {
                  const initials = ((s.nombre?.[0]||"")+(s.apellido?.[0]||"")).toUpperCase();
                  const rolClass = ROL_COLORS[s.rol] || "bg-gray-100 text-gray-700";
                  return (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                            {initials}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{s.nombre} {s.apellido}</div>
                            {s.especialidad && <div className="text-xs text-gray-500">{s.especialidad}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${rolClass}`}>
                          {ROL_LABELS[s.rol] || s.rol}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-gray-500 hidden md:table-cell">{s.email}</td>
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => toggleMutation.mutate({ id: s.id, activo: !s.is_active })}
                          className="flex items-center gap-1.5 mx-auto text-xs font-medium transition-colors"
                          title={s.is_active ? "Desactivar acceso" : "Activar acceso"}
                        >
                          {s.is_active
                            ? <><ToggleRight size={20} className="text-green-500"/><span className="text-green-600">Activo</span></>
                            : <><ToggleLeft size={20} className="text-gray-400"/><span className="text-gray-400">Inactivo</span></>
                          }
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
